import { useCallback, useRef, useState, type DragEvent } from 'react';
import { dragHasFiles, uploadAttachments, type UploadOutcome } from '../lib/attachUpload';

/**
 * Dépôt de pièces jointes sur un point : état d'envoi et retour à l'écran.
 * Partagé par le bloc « Pièces jointes » et la zone de réponse. Les pièces
 * vont au dossier du point (comme le V1), pas à un message.
 */
export function useAttachmentUpload(displayId: number, onUploaded: () => void) {
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<UploadOutcome | null>(null);

  const upload = useCallback(
    async (files: readonly File[]) => {
      if (files.length === 0) return;
      setBusy(true);
      setFeedback(null);
      try {
        const out = await uploadAttachments(displayId, files);
        setFeedback(out);
        if (out.ok > 0) onUploaded();
      } finally {
        setBusy(false);
      }
    },
    [displayId, onUploaded],
  );

  return { busy, feedback, upload, clearFeedback: () => setFeedback(null) };
}

/**
 * Cible de glisser-déposer de fichiers. Un glisser de texte ou de lien n'est
 * pas intercepté : le champ de saisie garde son comportement normal.
 */
export function useFileDrop(onFiles: (files: File[]) => void, enabled: boolean) {
  const [dragging, setDragging] = useState(false);
  if (!enabled) return { dragging: false, dropProps: {} };
  const dropProps = {
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!dragHasFiles(e.dataTransfer.types)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (!dragging) setDragging(true);
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      // Passer sur un élément enfant déclenche aussi dragleave : on ne
      // s'arrête que si le pointeur quitte vraiment la zone.
      const next = e.relatedTarget as Node | null;
      if (next && e.currentTarget.contains(next)) return;
      setDragging(false);
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      if (!dragHasFiles(e.dataTransfer.types)) return;
      e.preventDefault();
      setDragging(false);
      onFiles(Array.from(e.dataTransfer.files));
    },
  };
  return { dragging, dropProps };
}

/** Bouton trombone : ouvre le sélecteur de fichiers (plusieurs à la fois). */
export function AttachButton({
  onFiles,
  disabled,
  label = 'Joindre',
  title = 'Ajouter des fichiers aux pièces jointes du point (25 Mo max par fichier)',
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  label?: string;
  title?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className="btn ghost attach-btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title={title}
      >
        <span aria-hidden="true">📎</span> {label}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = ''; // permet de reprendre le même fichier
          onFiles(files);
        }}
      />
    </>
  );
}

/** Retour d'un dépôt : nombre ajouté, erreurs par fichier. */
export function UploadFeedback({ busy, feedback }: { busy: boolean; feedback: UploadOutcome | null }) {
  if (busy) {
    return (
      <div role="status" style={{ marginTop: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
        Envoi des fichiers…
      </div>
    );
  }
  if (!feedback) return null;
  return (
    <div role="status" style={{ marginTop: 6, fontSize: 'var(--fs-xs)' }}>
      {feedback.ok > 0 && (
        <span style={{ color: 'var(--success)' }}>
          ✓ {feedback.ok === 1 ? '1 fichier ajouté' : `${feedback.ok} fichiers ajoutés`} aux pièces jointes du point
        </span>
      )}
      {feedback.errors.length > 0 && (
        <ul style={{ color: 'var(--danger)', margin: '4px 0 0', paddingLeft: 16 }}>
          {feedback.errors.map((e, i) => (
            <li key={i} style={{ overflowWrap: 'anywhere' }}>
              {e.filename ? <strong>{e.filename}</strong> : 'Envoi'} : {e.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
